import React, { FC, useState } from 'react'
import Loader from 'uiKit/loaders/loader'
import { DaxUsagesResponse, GetTableListResponse } from 'tabs/data/api/generated/models'
import { useDatasetTables } from 'tabs/data/pages/descriptions/hooks/useDatasetTables'
import { DatasetTable } from './DatasetTable'
import { DatasetTablesHeader } from './DatasetTablesHeader'
import { alertError, alertSuccess } from 'api'
import { NewDatasetTable } from './NewDatasetTable'
import { createOption, Option } from 'helpers/selects'
import { useSearchParam } from 'react-use'
import { updateDatasetTable, UpdateDatasetTableParams } from 'tabs/data/api/tables'
import { cloneDeep } from 'lodash'
import { RelatedResourcesModal } from 'tabs/data/components/RelatedResourcesModal'

type DescriptionDatasetTablesProps = {
  datasetId?: string
}

export const DatasetTables: FC<DescriptionDatasetTablesProps> = ({ datasetId }) => {
  const searchParams = useSearchParam('search')
  const { data: tables = [], loading, createTable, refetch } = useDatasetTables(datasetId)

  const [search, setSearch] = useState(searchParams || '')

  const [warningData, setWarningData] = useState<null | DaxUsagesResponse>(null)
  const [forceUpdateParams, setForceUpdateParams] = useState<{
    tableId?: string
    params?: UpdateDatasetTableParams
  } | null>(null)

  let displayTables: GetTableListResponse['tables'] = tables
  if (search) {
    displayTables = tables.filter(({ tableName, tableDescription, metrics, columns }) => {
      const searchValue = search.toLowerCase()
      return (
        tableName.toLowerCase().includes(searchValue) ||
        tableDescription.toLowerCase().includes(searchValue) ||
        metrics
          .map(i => i.metricName)
          .join('|||| ')
          .toLowerCase()
          .includes(searchValue) ||
        columns
          .map(i => i.columnName)
          .join('|||| ')
          .toLowerCase()
          .includes(searchValue)
      )
    })
  }

  const tableOptions: Option[] = tables.map(({ tableName, tableId }) => createOption(tableName, tableId))

  const handleCreateTable = async (
    tableName: string,
    tableDescription: string,
    joinedTableIds: string[],
    tableSynonyms: string[],
  ) => {
    const created = await createTable(datasetId, {
      tableName,
      tableDescription,
      joinedTableIds,
      tableSynonyms,
    })
    if (created) await refetch()
  }

  const handleUpdateTable = async (tableId: string, params, forceUpdate = false) => {
    if (loading) return

    try {
      const result = await updateDatasetTable(tableId, datasetId, forceUpdate, params)
      if (result) {
        setWarningData(null)
        setForceUpdateParams(null)
        alertSuccess('Table updated', 3000)
        refetch()
      }
      return result
    } catch (error) {
      switch (error.response.status) {
        case 404:
          throw alertError('The table that was already deleted')
        case 409:
          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.usages) {
              setForceUpdateParams({ tableId, params: cloneDeep(params) })
              return setWarningData(errorData)
            }
          }

          throw alertError('Provided Table name already exists')
        case 412:
          setWarningData(null)
          throw alertError('Error! Someone already made changes. Please reload page and try again.')
        default:
          setWarningData(null)

          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.errorMessage) throw alertError(errorData?.errorMessage)
          }

          throw alertError(error?.message || error || 'Error updating table')
      }
    }
  }

  const handelForceUpdateTable = async () => {
    await handleUpdateTable(forceUpdateParams.tableId, forceUpdateParams.params, true)
    setForceUpdateParams(null)
  }

  return (
    <div style={{ paddingTop: '1rem' }}>
      <DatasetTablesHeader search={search} onChange={setSearch} count={tables?.length}>
        <NewDatasetTable options={tableOptions} onSubmit={handleCreateTable} />
      </DatasetTablesHeader>

      {!displayTables.length && loading && (
        <div style={{ margin: '3rem auto' }}>
          <Loader />
        </div>
      )}

      {displayTables.map(item => (
        <DatasetTable
          datasetId={datasetId}
          key={item?.tableId + datasetId}
          highlight={search}
          table={item}
          tableOptions={tableOptions}
          onChange={handleUpdateTable}
          refreshCallback={refetch}
        />
      ))}

      <RelatedResourcesModal
        open={Boolean(warningData?.usages)}
        data={warningData?.usages || {}}
        submitLabel="Save Anyway"
        message={`"${warningData?.name || 'Name'}" is used in the following places:`}
        onClose={() => setWarningData(null)}
        onSubmit={handelForceUpdateTable}
      />
    </div>
  )
}
